import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useImages } from '@/contexts/ImageContext'
import { logger } from '@/utils/logger'

const ImageLibrary: React.FC = () => {
  const { t } = useTranslation()
  const { images, refreshImages } = useImages()
  const [isLoading, setIsLoading] = useState(true)
  const [pollingForUpdates, setPollingForUpdates] = useState(false)
  logger.debug('Polling status:', pollingForUpdates) // Use the variable to avoid TS error

  // Debug logging
  logger.debug('ImageLibrary render - images count:', images.length)
  useEffect(() => {
    logger.debug('ImageLibrary useEffect - images changed:', images)
  }, [images])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFormat, setSelectedFormat] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'date' | 'filename' | 'size'>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<'selected' | string>('selected')

  const fetchImages = async () => {
    try {
      setIsLoading(true)
      logger.debug('Fetching images from backend...')

      // Fetch real images from standalone backend API - get all images (no pagination limit)
      const response = await fetch('/api/v1/imaging/standalone/list/?page_size=1000')

      if (response.ok) {
        const data = await response.json()
        logger.debug('Backend images loaded:', data.count, 'images')

        // Transform standalone backend data to frontend format
        if (data.results) {
          const transformedImages = data.results.map((slide: any) => {
            // Map standalone Slide model to ImageLibrary format
            const status = slide.status?.toLowerCase() || 'pending'

            logger.debug(`🔍 Mapping slide ${slide.id.substr(0,8)}: status=${status}`)

            return {
            id: slide.id,
            image_id: slide.id,
            filename: slide.name,
            image_type: 'WSI',
            image_format: slide.file_format?.replace('.', '').toUpperCase() || 'SVS',
            width: slide.width || 0,
            height: slide.height || 0,
            magnification: 0,
            slide_id: slide.slide_id || `SL-${slide.id.substr(0,8)}`,
            case_id: slide.case_accession || slide.case_number || null,
            file_size_mb: slide.file_size_mb || 0,
            resolution_mpx: Math.round((slide.width * slide.height) / 1000000 * 100) / 100 || 0,
            annotations_count: 0,
            created_at: slide.created_at || new Date().toISOString(),
            scanner_model: 'Unknown',
            scan_date: slide.created_at || new Date().toISOString(),
            status: status === 'completed' || status === 'ready' ? 'ready' : status === 'processing' ? 'processing' : status === 'failed' ? 'error' : 'pending',
            processing_status: status,
            processing_progress: status === 'completed' || status === 'ready' ? 100 : status === 'processing' ? 50 : 0,
            is_partial: false,
            // Case-centric workflow data
            patient_name: slide.patient_name || null,
            patient_code: slide.patient_code || null,
            specimen_code: slide.specimen_code || null,
            specimen_type: slide.specimen_type || null,
            anatomic_site: slide.anatomic_site || null
            }
          })

          logger.debug('Transformed images:', transformedImages)
          logger.debug('🔍 First image status debug:', {
            backendStatus: data.results[0]?.status,
            backendProcessingStatus: data.results[0]?.processing_status,
            frontendStatus: transformedImages[0]?.status,
            frontendProcessingStatus: transformedImages[0]?.processing_status
          })

          // Check for newly available partial images
          const newlyPartialImages = transformedImages.filter((img: any) =>
            img.is_partial &&
            !images.find(existingImg => existingImg.id === img.id && (existingImg as any).is_partial)
          )

          // Notify when images become viewable
          newlyPartialImages.forEach((img: any) => {
            toast.success(`⚡ ${img.filename} is now ready for preview!`, {
              duration: 6000,
              position: 'top-right'
            })
          })

          refreshImages(transformedImages)
        }
      } else {
        logger.debug('Backend not available, using context images')
        refreshImages()
      }
    } catch (error) {
      console.error('Error fetching images:', error)
      logger.debug('Falling back to context images')
      refreshImages()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchImages()
  }, [])

  // Real-time polling for processing images
  useEffect(() => {
    // Simple check - since we've mapped the status correctly above
    const processingImages = images.filter(img => img.status === 'processing')

    // Debug: Log all image statuses
    logger.debug('📊 All image statuses:', images.map(img => ({
      id: img.id,
      status: img.status,
      processing_status: (img as any).processing_status
    })))

    if (processingImages.length > 0) {
      logger.debug(`🔄 Found ${processingImages.length} processing images, starting polling...`)
      setPollingForUpdates(true)

      const pollInterval = setInterval(() => {
        logger.debug('📡 Polling for image updates...')
        fetchImages()
      }, 10000) // Poll every 10 seconds for processing updates

      return () => {
        clearInterval(pollInterval)
        setPollingForUpdates(false)
      }
    } else {
      setPollingForUpdates(false)
    }
  }, [images])

  const filteredImages = images.filter(image => {
    const matchesSearch = (image.filename || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (image.case_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (image.slide_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ((image as any).patient_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ((image as any).patient_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ((image as any).specimen_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ((image as any).anatomic_site || '').toLowerCase().includes(searchTerm.toLowerCase())

    const matchesFormat = selectedFormat === 'all' || (image.image_format || '').toUpperCase() === selectedFormat
    const matchesStatus = selectedStatus === 'all' || (image.status || '') === selectedStatus

    return matchesSearch && matchesFormat && matchesStatus
  })

  const sortedImages = [...filteredImages].sort((a, b) => {
    let comparison = 0

    switch (sortBy) {
      case 'date':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        break
      case 'filename':
        comparison = a.filename.localeCompare(b.filename)
        break
      case 'size':
        comparison = a.file_size_mb - b.file_size_mb
        break
    }

    return sortOrder === 'desc' ? -comparison : comparison
  })

  const formatFileSize = (mb: number): string => {
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    return `${(mb / 1024).toFixed(1)} GB`
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string, isPartial: boolean = false) => {
    if (isPartial) return 'text-orange-600 bg-orange-100'
    switch (status) {
      case 'ready': return 'text-green-600 bg-green-100'
      case 'processing': return 'text-yellow-600 bg-yellow-100'
      case 'error': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string, isPartial: boolean = false) => {
    if (isPartial) return '⚡'
    switch (status) {
      case 'ready': return '✅'
      case 'processing': return '⚙️'
      case 'error': return '❌'
      default: return '⏳'
    }
  }

  const handleSelectImage = (imageId: string) => {
    setSelectedImages(prev =>
      prev.includes(imageId)
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    )
  }

  const handleSelectAll = () => {
    if (selectedImages.length === sortedImages.length) {
      setSelectedImages([])
    } else {
      setSelectedImages(sortedImages.map(img => img.id))
    }
  }

  const handleDeleteSelected = () => {
    if (selectedImages.length === 0) return
    setDeleteTarget('selected')
    setShowDeleteConfirm(true)
  }

  const handleDeleteSingle = (imageId: string) => {
    setDeleteTarget(imageId)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    try {
      const imagesToDelete = deleteTarget === 'selected' ? selectedImages : [deleteTarget]
      const imageNames = imagesToDelete.map(id => {
        const img = images.find(i => i.id === id)
        return img?.filename || 'Unknown'
      }).join(', ')

      // Delete from backend
      const deletePromises = imagesToDelete.map(async (imageId) => {
        const response = await fetch(`/api/v1/imaging/slides/${imageId}/`, {
          method: 'DELETE',
        })

        if (!response.ok) {
          throw new Error(`Failed to delete image ${imageId}`)
        }
        return imageId
      })

      // Wait for all deletions to complete
      await Promise.all(deletePromises)

      // Remove from frontend state
      const updatedImages = images.filter(img => !imagesToDelete.includes(img.id))
      refreshImages(updatedImages)

      setSelectedImages([])
      setShowDeleteConfirm(false)

      // Show success toast
      toast.success(
        imagesToDelete.length > 1
          ? `${imagesToDelete.length} images deleted successfully`
          : `${imageNames} deleted successfully`,
        {
          duration: 3000,
          position: 'top-right'
        }
      )

      // Refresh images from backend to ensure sync
      fetchImages()

    } catch (error) {
      console.error('Error deleting images:', error)
      toast.error('Failed to delete images. Please try again.')
    }
  }


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('image.imageLibrary')}</h1>
            <p className="text-gray-600">
              {t('image.manageImages')}
            </p>
          </div>
          <div className="flex space-x-3">
            <Link
              to="/imaging/upload"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              📤 Upload Images
            </Link>
            {selectedImages.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
              >
                🗑️ {t('common.delete')} ({selectedImages.length})
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{images.length}</div>
            <div className="text-sm text-gray-600">{t('image.title')}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {images.filter(img => img.status === 'ready').length}
            </div>
            <div className="text-sm text-gray-600">{t('image.ready')}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-yellow-600">
              {images.filter(img => img.status === 'processing').length}
            </div>
            <div className="text-sm text-gray-600">{t('image.processing')}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">
              {formatFileSize(images.reduce((total, img) => total + img.file_size_mb, 0))}
            </div>
            <div className="text-sm text-gray-600">Total Size</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Formats</option>
              <option value="SVS">SVS</option>
              <option value="NDPI">NDPI</option>
              <option value="SCN">SCN</option>
              <option value="TIFF">TIFF</option>
              <option value="DICOM">DICOM</option>
            </select>
          </div>
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="ready">{t('image.ready')}</option>
              <option value="processing">{t('image.processing')}</option>
              <option value="error">{t('image.error')}</option>
            </select>
          </div>
          <div className="flex space-x-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'filename' | 'size')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="date">{t('image.uploadDate')}</option>
              <option value="filename">{t('image.fileName')}</option>
              <option value="size">{t('image.fileSize')}</option>
            </select>
            <button
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {sortOrder === 'asc' ? '⬆️' : '⬇️'}
            </button>
          </div>
        </div>
      </div>

      {/* Image Grid */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Table Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={selectedImages.length === sortedImages.length && sortedImages.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                {sortedImages.length} {t('image.title').toLowerCase()} {searchTerm && '(filtered)'}
              </span>
            </div>
          </div>
        </div>

        {/* Image List */}
        <div className="divide-y divide-gray-200">
          {sortedImages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm ? `No ${t('image.title').toLowerCase()} match your search criteria` : `No ${t('image.title').toLowerCase()} in library`}
            </div>
          ) : (
            sortedImages.map((image) => (
              <div key={image.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedImages.includes(image.id)}
                    onChange={() => handleSelectImage(image.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                  />

                  {/* Column 1: Image Info */}
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {image.filename}
                    </h3>
                    <div className="flex items-center gap-x-3 text-xs text-gray-600">
                      <span className="font-medium">{image.width} × {image.height}</span>
                      <span className="font-medium">{formatFileSize(image.file_size_mb)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(image.status, (image as any).is_partial)}`}>
                        {getStatusIcon(image.status, (image as any).is_partial)} {(image as any).is_partial ? 'partial' : t(`image.${image.status}`)}
                      </span>
                    </div>
                  </div>

                  {/* Column 2: Case/Patient/Specimen Links */}
                  <div className="flex-1">
                    <div className="space-y-1 text-xs">
                      {(image as any).patient_name && (
                        <div>
                          <Link
                            to="/pathology/patients"
                            state={{ highlightId: (image as any).patient_id }}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            👤 {(image as any).patient_name} ({(image as any).patient_code})
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Column 3: Actions */}
                  <div className="flex-shrink-0">
                    <div className="flex flex-col items-end space-y-2">
                      {/* Action buttons */}
                      <div className="flex space-x-2">
                        {image.status === 'ready' && (() => {
                          const isViewableFormat = ['SVS', 'NDPI', 'VSI', 'SCN', 'TIFF', 'TIF', 'JPEG', 'JPG', 'PNG'].includes(
                            (image.image_format || '').toUpperCase()
                          )
                          const hasValidDimensions = (
                            image.width && image.height &&
                            image.width > 0 && image.height > 0 &&
                            !isNaN(image.width) && !isNaN(image.height) &&
                            image.width >= 10 && image.height >= 10
                          )

                          if (isViewableFormat && hasValidDimensions) {
                            return (
                              <Link
                                to={`/imaging/viewer/${image.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                title={t('image.viewImage')}
                              >
                                🔬 {t('common.view')}
                              </Link>
                            )
                          }
                          return null
                        })()}
                        {image.status === 'ready' && (
                          <a
                            href={`/api/v1/imaging/slides/${image.id}/download/`}
                            download={image.filename}
                            className="px-3 py-1 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                            title="Download image"
                          >
                            📥 Download
                          </a>
                        )}
                        <button
                          onClick={() => handleDeleteSingle(image.id)}
                          className="px-3 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200"
                          title={t('image.deleteImage')}
                        >
                          🗑️
                        </button>
                      </div>

                      {/* Date */}
                      <span className="text-xs text-gray-400">
                        {formatDate(image.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {deleteTarget === 'selected' ? 'Remove Selected Images?' : 'Remove Image?'}
            </h3>
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">
                {deleteTarget === 'selected'
                  ? `This will remove ${selectedImages.length} selected image${selectedImages.length > 1 ? 's' : ''} from your view.`
                  : 'This will remove the selected image from your view.'
                }
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <div className="text-red-400">⚠️</div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      <strong>Warning:</strong> This action will permanently delete the selected images and all associated files from the server.
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                {deleteTarget === 'selected' ? t('common.delete') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageLibrary